// ============================================================
// Star Line — Premium Calendar Date Picker
// Custom dropdown calendar with month navigation
// ============================================================

import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

interface DatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  min?: string; // YYYY-MM-DD
  max?: string; // YYYY-MM-DD
  label?: string;
  ariaLabel?: string;
  /** Shown when value is empty */
  placeholder?: string;
  /** 'default' = full-size SearchForm trigger, 'compact' = inline admin/filter bar size */
  variant?: 'default' | 'compact';
  className?: string;
}

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Format YYYY-MM-DD → DD-MM-YYYY for display */
function formatDateDMY(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
}

/** Pad number to 2 digits */
function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/** Build YYYY-MM-DD string */
function toISO(year: number, month: number, day: number): string {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

/** Get month name */
function monthName(month: number): string {
  return [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ][month];
}

interface DayCell {
  day: number;
  month: number; // 0-indexed
  year: number;
  iso: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

export default function DatePicker({ value, onChange, min, max, ariaLabel, placeholder, variant = 'default', className }: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Parse initial date for the calendar view
  const parsed = useMemo(() => {
    if (!value) return new Date();
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [value]);

  const [viewYear, setViewYear] = useState(parsed.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed.getMonth());

  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  /**
   * Which way the calendar opens.
   *
   * It is 300px wide and ~400px tall, and its trigger usually sits at the far
   * right of a toolbar — so anchoring it to the trigger's left edge pushed it
   * off the side of the window and the last three columns of the month were
   * simply cut off. Measured against the viewport on open, so it flips to
   * whichever edge it fits on rather than guessing from the variant.
   */
  const [placement, setPlacement] = useState({ alignRight: false, above: false });

  useLayoutEffect(() => {
    if (!isOpen) return;
    const trigger = containerRef.current?.getBoundingClientRect();
    const menu = menuRef.current?.getBoundingClientRect();
    if (!trigger || !menu) return;
    const margin = 8;
    setPlacement({
      alignRight:
        trigger.left + menu.width > window.innerWidth - margin
        && trigger.right - menu.width > margin,
      above:
        trigger.bottom + menu.height > window.innerHeight - margin
        && trigger.top - menu.height > margin,
    });
  }, [isOpen]);

  // When value changes externally, update view to show that month
  useEffect(() => {
    if (value) {
      const [y, m] = value.split('-').map(Number);
      setViewYear(y);
      setViewMonth(m - 1);
    }
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Keyboard support
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // ── Build calendar grid ──────────────────────────────────
  const days = useMemo((): DayCell[] => {
    const today = new Date();
    const todayISO = toISO(today.getFullYear(), today.getMonth(), today.getDate());

    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const startDay = firstOfMonth.getDay(); // 0 = Sunday
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    // Previous month overflow
    const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1;
    const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
    const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

    const cells: DayCell[] = [];

    // Previous month filler days
    for (let i = startDay - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      const iso = toISO(prevYear, prevMonth, d);
      cells.push({
        day: d,
        month: prevMonth,
        year: prevYear,
        iso,
        isCurrentMonth: false,
        isToday: iso === todayISO,
        isSelected: iso === value,
        isDisabled: !!(min && iso < min) || !!(max && iso > max),
      });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = toISO(viewYear, viewMonth, d);
      cells.push({
        day: d,
        month: viewMonth,
        year: viewYear,
        iso,
        isCurrentMonth: true,
        isToday: iso === todayISO,
        isSelected: iso === value,
        isDisabled: !!(min && iso < min) || !!(max && iso > max),
      });
    }

    // Next month filler days (fill to 42 cells = 6 rows)
    const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1;
    const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      const iso = toISO(nextYear, nextMonth, d);
      cells.push({
        day: d,
        month: nextMonth,
        year: nextYear,
        iso,
        isCurrentMonth: false,
        isToday: iso === todayISO,
        isSelected: iso === value,
        isDisabled: !!(min && iso < min) || !!(max && iso > max),
      });
    }

    return cells;
  }, [viewYear, viewMonth, value, min, max]);

  const prevMonth = useCallback(() => {
    setViewMonth(prev => {
      if (prev === 0) {
        setViewYear(y => y - 1);
        return 11;
      }
      return prev - 1;
    });
  }, []);

  const nextMonth = useCallback(() => {
    setViewMonth(prev => {
      if (prev === 11) {
        setViewYear(y => y + 1);
        return 0;
      }
      return prev + 1;
    });
  }, []);

  const selectDate = useCallback((day: DayCell) => {
    if (day.isDisabled) return;
    onChange(day.iso);
    setIsOpen(false);
  }, [onChange]);

  // Check if we can go to previous month (don't go before min date's month)
  const canGoPrev = useMemo(() => {
    if (!min) return true;
    const [my, mm] = min.split('-').map(Number);
    return viewYear > my || (viewYear === my && viewMonth > mm - 1);
  }, [min, viewYear, viewMonth]);

  // Check if we can go to next month (don't go past max date's month)
  const canGoNext = useMemo(() => {
    if (!max) return true;
    const [my, mm] = max.split('-').map(Number);
    return viewYear < my || (viewYear === my && viewMonth < mm - 1);
  }, [max, viewYear, viewMonth]);

  const isCompact = variant === 'compact';

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      {/* ── Trigger button ──────────────────────────────── */}
      <button
        type="button"
        title={ariaLabel || 'Select date'}
        aria-label={ariaLabel}
        onClick={() => setIsOpen(prev => !prev)}
        className={isCompact
          ? `flex items-center gap-2 bg-secondary/40 border border-border/30 text-foreground rounded-lg text-sm cursor-pointer transition-all duration-150
             focus:outline-none focus:ring-2 focus:ring-primary/20 hover:bg-secondary/70 tabular-nums
             h-9 px-3
             ${isOpen ? 'ring-2 ring-primary/20 bg-secondary/70' : ''}`
          : `w-full bg-secondary text-foreground rounded-lg pl-10 pr-9 py-3 text-base font-medium text-left cursor-pointer transition-all duration-150
             focus:outline-none focus:ring-2 focus:ring-primary/50 hover:bg-secondary/80
             ${isOpen ? 'ring-2 ring-primary/50 bg-secondary/80' : ''}`
        }
      >
        {/* Calendar icon */}
        {!isCompact && (
          <Calendar
            className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-150 ${
              isOpen ? 'text-primary' : 'text-muted-foreground'
            }`}
          />
        )}
        {isCompact && (
          <Calendar className={`w-4 h-4 shrink-0 transition-colors duration-150 ${isOpen ? 'text-primary' : 'text-muted-foreground'}`} />
        )}

        {/* Formatted date */}
        {value ? (
          <span className={isCompact ? 'tabular-nums' : 'block truncate'}>{formatDateDMY(value)}</span>
        ) : (
          <span className={isCompact ? 'text-muted-foreground/60 text-xs' : 'block truncate text-muted-foreground'}>
            {placeholder || ariaLabel || 'Select date'}
          </span>
        )}

        {/* Animated chevron */}
        <motion.span
          className={isCompact
            ? 'flex items-center justify-center ml-1'
            : 'absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center'
          }
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25, mass: 0.5 }}
        >
          <ChevronDown
            className={`w-4 h-4 transition-colors duration-150 ${
              isOpen ? 'text-primary' : 'text-muted-foreground/70'
            }`}
          />
        </motion.span>
      </button>

      {/* ── Calendar dropdown ───────────────────────────── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30, mass: 0.4 }}
            style={{ transformOrigin: placement.above ? 'bottom center' : 'top center' }}
            className={`absolute z-50 min-w-[300px] bg-card border border-border/60 rounded-2xl shadow-2xl shadow-black/25 overflow-hidden backdrop-blur-sm p-4
              ${placement.above ? 'bottom-full mb-2' : 'top-full mt-2'}
              ${placement.alignRight ? 'right-0' : isCompact ? 'left-0' : 'left-0 right-0'}`}
          >
            {/* ── Month/Year header ─────────────────────── */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={prevMonth}
                disabled={!canGoPrev}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Previous month"
              >
                <ChevronLeft className="w-4 h-4 text-foreground" />
              </button>
              <span className="font-display text-sm font-bold text-foreground select-none">
                {monthName(viewMonth)} {viewYear}
              </span>
              <button
                type="button"
                onClick={nextMonth}
                disabled={!canGoNext}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Next month"
              >
                <ChevronRight className="w-4 h-4 text-foreground" />
              </button>
            </div>

            {/* ── Day-of-week headers ───────────────────── */}
            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {DAY_LABELS.map((d, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center h-8 text-xs font-semibold text-muted-foreground/60 select-none"
                >
                  {d}
                </div>
              ))}
            </div>

            {/* ── Day grid ──────────────────────────────── */}
            <div className="grid grid-cols-7 gap-0.5">
              {days.map((day, i) => {
                const baseClasses = 'relative flex items-center justify-center h-9 w-full rounded-lg text-sm font-medium transition-all duration-100 select-none';

                if (day.isDisabled) {
                  return (
                    <div
                      key={i}
                      className={`${baseClasses} text-muted-foreground/25 cursor-not-allowed`}
                    >
                      {day.day}
                    </div>
                  );
                }

                if (day.isSelected) {
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => selectDate(day)}
                      className={`${baseClasses} bg-foreground text-background font-bold rounded-xl shadow-lg`}
                    >
                      {day.day}
                    </button>
                  );
                }

                if (day.isToday && day.isCurrentMonth) {
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => selectDate(day)}
                      className={`${baseClasses} bg-primary/15 text-primary font-bold hover:bg-primary/25 rounded-xl`}
                    >
                      {day.day}
                      {/* Today dot */}
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                    </button>
                  );
                }

                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => selectDate(day)}
                    className={`${baseClasses} cursor-pointer hover:bg-secondary/80 active:scale-95
                      ${day.isCurrentMonth
                        ? 'text-foreground'
                        : 'text-muted-foreground/35'
                      }`}
                  >
                    {day.day}
                  </button>
                );
              })}
            </div>

            {/* ── Quick actions ──────────────────────────── */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/30">
              <button
                type="button"
                onClick={() => {
                  const today = new Date();
                  const iso = toISO(today.getFullYear(), today.getMonth(), today.getDate());
                  if (!min || iso >= min) {
                    onChange(iso);
                    setIsOpen(false);
                  }
                }}
                className="text-xs font-medium text-primary hover:text-primary/80 transition-colors px-2 py-1 rounded-md hover:bg-primary/10"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => {
                  const tmrw = new Date();
                  tmrw.setDate(tmrw.getDate() + 1);
                  const iso = toISO(tmrw.getFullYear(), tmrw.getMonth(), tmrw.getDate());
                  onChange(iso);
                  setIsOpen(false);
                }}
                className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-secondary/60"
              >
                Tomorrow
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
